import { applyCompanyEditRefresh } from 'src/composables/app/useCompanyForm'
import { companyService } from 'src/services/api/company.service'
import { log } from 'src/services/app/log.service'
import { notifyService } from 'src/services/app/notify.service'
import { NOTIFICATION_MESSAGES } from 'src/services/consts/notification.const'
import { ROUTES } from 'src/services/consts/routes.const'

// Registry to store active company and product queries
const queryRegistry = {
  companies: {}, // companyId -> { query, data }
  products: {}, // productId -> { query, data, companyId },
}

const isSameCompanyId = (a, b) => String(a) === String(b)

const getCompanyQueryEntry = (companyId) =>
  queryRegistry.companies[companyId] ?? queryRegistry.companies[String(companyId)]

const isOnCompanyEditPage = (route, companyId) =>
  route.name === ROUTES.COMPANY_EDIT && isSameCompanyId(route.params.id, companyId)

export const notificationService = {
  getQueryRegistry: () => queryRegistry,

  registerCompanyQuery: (companyId, query, data) => {
    if (companyId && query) {
      queryRegistry.companies[companyId] = { query, data }
    }
  },

  registerProductQuery: (productId, companyId, query, data) => {
    if (productId && companyId && query) {
      queryRegistry.products[productId] = { query, data, companyId }
    }
  },

  unregisterCompanyQuery: (companyId) => {
    if (companyId && queryRegistry.companies[companyId]) {
      delete queryRegistry.companies[companyId]
    }
  },

  unregisterProductQuery: (productId) => {
    if (productId && queryRegistry.products[productId]) {
      delete queryRegistry.products[productId]
    }
  },

  getCompanyData: async (companyId) => {
    try {
      const company = await companyService.getCompany(companyId)
      return company
    } catch (error) {
      log.error(`Failed to fetch company data for fallback: ${error}`)
      return null
    }
  },

  getProductData: async (productId, companyId) => {
    try {
      if (!companyId) {
        log.error('Cannot fetch product without company ID')
        return null
      }

      const company = await companyService.getCompany(companyId)
      const product = company?.products?.find((p) => p.id === productId)
      return product
    } catch (error) {
      log.error(`Failed to fetch product data for fallback: ${error}`)
      return null
    }
  },

  createRefreshAction: ({
    router,
    route,
    notification,
    successMessage = NOTIFICATION_MESSAGES.success.refreshData,
  }) => {
    // Return a function that will check the registry at execution time
    return async () => {
      const onEditPage = isOnCompanyEditPage(route, notification.companyId)
      const isCurrentPath = route.fullPath === notification.path

      let registeredEntity = null
      if (!notification.productId) {
        registeredEntity = getCompanyQueryEntry(notification.companyId)
      } else {
        registeredEntity =
          queryRegistry.products[notification.productId] ??
          queryRegistry.products[String(notification.productId)]
      }

      if (registeredEntity?.query) {
        await registeredEntity.query.refetch()

        if (onEditPage) {
          applyCompanyEditRefresh()
        } else if (!isCurrentPath) {
          router.push(notification.path)
        }

        notifyService.success(successMessage)
        return
      }

      if (onEditPage) {
        const companyEntry = getCompanyQueryEntry(notification.companyId)
        if (companyEntry?.query) {
          await companyEntry.query.refetch()
          applyCompanyEditRefresh()
        }
        notifyService.success(successMessage)
        return
      }

      if (isCurrentPath) {
        router.go(0)
      } else {
        router.push(notification.path)
      }
      notifyService.success(NOTIFICATION_MESSAGES.success.navigated)
    }
  },

  handleViewingWithoutQuery: (router, isViewing) => {
    if (isViewing) {
      setTimeout(() => router.go(0), 500)
    }
  },
}
